import { z } from 'zod';
import { api } from './api';
import {
  unmappedRowSchema,
  importChangeSchema,
  importDetailSchema,
  importSchema,
  qualityReportSchema,
  type ImportChange,
  type ImportDetail,
  type ImportJob,
  type QualityReport,
} from '@/types/schemas';

export const importsService = {
  list: (signal?: AbortSignal): Promise<ImportJob[]> =>
    api.get('/imports', z.array(importSchema), signal),

  upload: (file: File): Promise<ImportDetail> => {
    const form = new FormData();
    form.append('file', file);
    return api.upload('/imports', importDetailSchema, form);
  },

  detail: (id: string, signal?: AbortSignal): Promise<ImportDetail> =>
    api.get(`/imports/${id}`, importDetailSchema, signal),

  quality: (id: string, signal?: AbortSignal): Promise<QualityReport> =>
    api.get(`/imports/${id}/quality`, qualityReportSchema, signal),

  unmapped: (id: string, signal?: AbortSignal) =>
    api.get(`/imports/${id}/unmapped`, z.array(unmappedRowSchema), signal),

  changes: (id: string, signal?: AbortSignal): Promise<ImportChange[]> =>
    api.get(`/imports/${id}/changes`, z.array(importChangeSchema), signal),

  mapSheet: (id: string, sheetId: string, businessUnitId: string): Promise<ImportDetail> =>
    api.post(`/imports/${id}/mappings/sheets`, importDetailSchema, {
      sheet_id: sheetId,
      business_unit_id: businessUnitId,
      remember: true,
    }),

  mapCategory: (id: string, rawLabel: string, categoryId: string): Promise<ImportDetail> =>
    api.post(`/imports/${id}/mappings/categories`, importDetailSchema, {
      raw_label: rawLabel,
      financial_category_id: categoryId,
      remember: true,
    }),

  confirm: (id: string): Promise<ImportDetail> =>
    api.post(`/imports/${id}/confirm`, importDetailSchema),

  rollback: (id: string): Promise<ImportDetail> =>
    api.post(`/imports/${id}/rollback`, importDetailSchema),

  /** Owner-only: run a rolled-back import's own file through again, so the
   * corrected catalogue applies to it. Backend enforces both the permission
   * and the ROLLED_BACK precondition. */
  reimport: (id: string): Promise<ImportDetail> =>
    api.post(`/imports/${id}/reimport`, importDetailSchema),

  originalUrl: (id: string) => `${import.meta.env.VITE_API_URL ?? '/api/v1'}/imports/${id}/original`,
};
